import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, ChangeDetectorRef } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs/internal/Subscription';
import { PaginationPageEvent } from '@snapon/design-system-angular';
import { AppInfoService } from 'core/app-info/app-info.service';
import { AppStateService } from 'core/app-state/app-state.service';
import { ConfidenceToClose, ConfidenceToCloseNames } from 'src/app/modules/shared/entities/confidence-to-close.enum';
import { Quote, QuoteListResponse } from 'src/app/modules/pos/quotes/entities/quote-list.class';
import { LastQuotesFilters } from 'src/app/modules/marketing/products/entities/last-quotes-filters.class';
import { AppLocalization } from 'src/app/modules/shared/entities/app-localization';
import { LastQuoteListItem } from 'src/app/modules/marketing/products/entities/quote-list-item.class';
import { StorageValues } from 'src/app/modules/shared/entities/storage-values.enum';
import { QuotesService } from './quotes.service';

@Component({
  selector: 'app-quotes-widget',
  templateUrl: './quotes-widget.component.html',
  styleUrls: ['./quotes-widget.component.scss'],
  standalone: false
})
export class QuotesWidgetComponent implements OnInit, OnDestroy, OnChanges {
  @Input() customerId: string;
  @Input() customerName: string;
  @Input() leadId: string;
  @Input() accountManagerEmployeeId: string;
  @Output() loaded = new EventEmitter<void>();

  appStrings: AppLocalization;
  dateFormat: string;
  quotesList: LastQuoteListItem[];
  currencySymbol: string;
  noOpenQuotesTitle: string;
  electronAppOutOfSync = false;
  quotesFilters: LastQuotesFilters;
  private subs = new Subscription();
  loading = false;
  pageSize = 15;
  pageOffset = 0;
  totalRecords = 0;
  totalPages = 0;
  displayedColumns: string[] = [
    'QuoteName',
    'CustomerName',
    'Confidence',
    'Number',
    'ExpireDate',
    'Total',
    'Edit'
  ];
  title: string;
  panelExpanded = false;
  confidenceToClose = ConfidenceToClose;
  confidenceToCloseNames = ConfidenceToCloseNames;

  constructor(
    private appInfoService: AppInfoService,
    private appStateService: AppStateService,
    private quotesService: QuotesService,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit() {
    this.appStrings = this.appInfoService.appStrings;
    this.dateFormat = this.appInfoService.dateFormat;
    this.currencySymbol = this.appInfoService.currencySymbol;
    this.noOpenQuotesTitle = this.appStrings.menu_quotesNoOpenQuotes;
    this.electronAppOutOfSync = this.appStateService.checkIfElectronAppIsOutOfSync();

    this.subs.add(this.appStateService.offlineStateToggle$.subscribe(isOnline => {
      if (!isOnline) {
        this.getQuoteList(null);
      }
    }
    ));

    this.getQuoteList();
  }

  ngOnChanges() {
    this.getQuoteList();
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  /**
   * Getting quotes for an AM or for customer/lead
   * @param filters filters
   */
  getQuoteList(filters?: LastQuotesFilters) {
    if (filters) {
      this.quotesFilters = filters;
    }
    if (this.accountManagerEmployeeId) {
      this.loading = true;
      this.quotesService.getLastQuotes(filters, this.accountManagerEmployeeId).subscribe({
        next: (result) => {
          const allQuotes = filters ? this.reduceToTenResults(result) : result.quotes;
          this.setValues(allQuotes);
          this.onLoadFinished();
        },
        error: () => this.onLoadFinished()
      });
      return;
    }

    if (this.customerId || this.leadId) {
      this.getQuotesForAccounts(this.customerId ? this.customerId : this.leadId, filters);
    }
  }

  /**
   * Get quotes for a customer or a lead
   * @param id customer or lead id
   * @param filters filters
   */
  getQuotesForAccounts(id: string, filters?: LastQuotesFilters) {
    this.loading = true;

    // When there are filters applied, there are two search calls, for partial and exact search
    // That's why when there are filters,the response is an array of 2 objects having quotes property and they are concatenated
    this.quotesService.getLastQuotes(filters, null, id, this.pageOffset).subscribe({
      next: (response: any) => {
        const quotes = filters ? response[0].quotes.concat(response[1].quotes) : response.quotes;
        this.totalRecords = filters ? response[0].count + response[1].count : response.count;
        this.setValues(quotes);
        this.onLoadFinished();
      },
      error: () => this.onLoadFinished()
    });
  }

  private onLoadFinished() {
    this.loading = false;
    this.loaded.emit();
  }

  /**
   * Preventing default when clicking on panel's header
   * @param event click event
   */
  expandPanel(event) {
    event.preventDefault();
    this.panelExpanded = !this.panelExpanded;
    this.cdr.detectChanges();
  }

  /**
   * Emits tab number as active tab
   * @param event click event
   */
  navigateToOpenQuotes(event: Event) {
    event.stopPropagation();

    // Set filter in sessionStorage for quotes page
    const filterValues: any = {
      displayedSearchValue: '',
      quotesShowFor: 1
    };

    if (this.customerName) {
      filterValues.customerName = this.customerName;
      filterValues.displayedSearchValue = this.customerName;
    }

    // Store filter in sessionStorage with the correct key
    sessionStorage.setItem(
      StorageValues.CachedFilters + StorageValues.QuoteKey,
      JSON.stringify(filterValues)
    );

    this.router.navigate(['/quotes']);
  }

  /**
   * Navigate to new quote for customer
   */
  navigateToNewQuote() {
    this.router.navigate(['/quotes', this.customerId, 'new']);
  }

  /**
   * Navigate to new quote for lead
   */
  navigateToNewLeadQuote() {
    this.router.navigate(['/quotes/lead/', this.leadId, 'new']);
  }

  /**
   * Navigate to new generic quote
   */
  navigateToNewGenericQuote() {
    this.router.navigate(['/quotes', 'new']);
  }

  /**
   * Setting values for quotes list
   * @param quotes quotes list
   */
  private setValues(quotes: LastQuoteListItem[]) {
    this.quotesList = quotes;
    this.totalPages = Math.ceil(this.totalRecords / this.pageSize);

    if (this.totalRecords) {
      const commonTitle = `${this.appStrings.menu_openQuotes} (${this.totalRecords})`;
      if (this.accountManagerEmployeeId) {
        this.title = `${this.appStrings.menu_openQuotesLast} ${commonTitle}`;
      } else {
        this.title = commonTitle;
      }
    } else {
      this.title = this.noOpenQuotesTitle;
    }
  }

  /**
   * Redirecting to quote details page
   * @param quote quote id
   */
  goToQuoteDetails(quote: Quote) {
    this.router.navigate(['quotes', quote.id]);
  }

  /**
   * Reduces quotes results to 10 when there are more exact+partial results
   * @param quoteResponse array with exact and partial responses
   * @returns an array of 10 or less quotes
   */
  reduceToTenResults(quoteResponses: QuoteListResponse[]) {
    let lastTenQuotes;
    const maxLength = 10;
    // Exact search quotes response
    const exactQuotesLength = quoteResponses[0].quotes.length;
    // Partial search quotes response
    const partialQuotesLength = quoteResponses[1].quotes.length;

    if (exactQuotesLength === maxLength) {
      lastTenQuotes = quoteResponses[0].quotes;
    } else if (exactQuotesLength < maxLength && partialQuotesLength) {
      const reducedPartial = quoteResponses[1].quotes.splice(0, maxLength - exactQuotesLength);
      lastTenQuotes = quoteResponses[0].quotes.concat(reducedPartial);
    } else {
      lastTenQuotes = quoteResponses[0].quotes;
    }

    return lastTenQuotes;
  }

  /**
   * Handle page change event
   * @param event page event
   */
  loadPage(event: PaginationPageEvent) {
    this.pageOffset = (event.pageNumber - 1) * this.pageSize;
    this.getQuoteList();
  }

}
